import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { formatPln, parsePln, plnToInput } from '../catalog/money';
import { ConflictNotice } from '../ui/ConflictNotice';
import { DialogLayer } from '../ui/Dialog';
import { trapTab } from '../ui/focus';
import { QUOTES_KEY } from '../quotes/types';
import { MAX_QUANTITY, packlistKey, type CustomLineView } from './types';

/**
 * Owner 2026-11-04 („pozycja własna”): a line from outside the catalogs. It stands with our gear without a category,
 * marked „pozycja własna”, reserves nothing (no item behind it) and goes to the quote like the rest. Its price is the
 * quote's „Cena”: shown when the server sends it (whoever may see this event's quote), asked only from whoever may give it.
 */
export function CustomLineRow({ line, sectionCell, cols, editable, showPrice, onEdit, onRemove }: {
  line: CustomLineView;
  sectionCell: ReactNode;
  cols: { select: boolean; quantity: boolean; window: boolean; notes: boolean };
  editable: boolean;
  showPrice: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  return (
    <tr className="sheet-row pl-row pl-custom" data-custom-id={line.id} title="Pozycja własna — spoza katalogu, nie rezerwuje magazynu. W kosztorysie pojawia się jak reszta sprzętówki.">
      <th scope="row" className="sheet-section-cell">
        {sectionCell}
      </th>
      {cols.select && <td className="pl-check" />}
      <td className="sheet-main pl-name">
        <span className="pl-item">{line.name}</span>
        <span className="pl-sub" data-feature-id="packlists.custom">
          pozycja własna
          {showPrice && line.priceGrosze !== undefined && (line.priceGrosze === null ? <> · <span className="quote-flag">bez ceny</span></> : ` · ${formatPln(line.priceGrosze)}`)}
        </span>
      </td>
      {cols.quantity && (
        <td className="num pl-qty" data-label="Ilość">
          {line.quantity ?? ''}
        </td>
      )}
      {cols.window && <td className="pl-window" data-label="Okno" />}
      <td className="pl-res" data-label="Rezerwacja">
        <span className="muted">bez rezerwacji</span>
      </td>
      {cols.notes && <td className="sheet-note pl-notes" />}
      {editable && (
        <td className="q-actions">
          <button className="link-button pl-custom-edit" type="button" aria-label={`Zmień pozycję własną: ${line.name}`} onClick={onEdit}>
            zmień
          </button>
          <button className="btn icon ghost q-remove" type="button" aria-label={`Usuń ze sprzętówki: ${line.name}`} title="Usuń ze sprzętówki" onClick={onRemove}>
            ×
          </button>
        </td>
      )}
    </tr>
  );
}

/** „+ Pozycja własna” and „zmień”: name, quantity (> 0) and — for whoever sees prices — the price (required). */
export function CustomLineDialog({ eventId, line, showPrice, categories = [], onClose }: {
  eventId: string;
  /** null: a new one */
  line: CustomLineView | null;
  showPrice: boolean;
  /** Owner 2026-11-04 19:30: the catalog's categories (as the sheet groups by them); none chosen: „Bez kategorii”. */
  categories?: ReadonlyArray<{ id: string; label: string }>;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(line?.name ?? '');
  const [quantity, setQuantity] = useState(line?.quantity !== undefined ? String(line.quantity) : '1');
  const [price, setPrice] = useState(line && typeof line.priceGrosze === 'number' ? plnToInput(line.priceGrosze) : '');
  const [categoryId, setCategoryId] = useState(line?.categoryId ?? '');
  const [invalid, setInvalid] = useState('');
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      line ? api(`/api/packlists/custom-lines/${line.id}`, { method: 'PATCH', body: { ...body, version: line.version } }) : api(`/api/packlists/${eventId}/custom-lines`, { method: 'POST', body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: packlistKey(eventId) });
      void queryClient.invalidateQueries({ queryKey: QUOTES_KEY });
      onClose();
    },
  });
  const error = save.error instanceof ApiError ? save.error : null;

  function submit(e: FormEvent) {
    e.preventDefault();
    const body: Record<string, unknown> = {};
    if (name.trim() === '') return setInvalid('Podaj nazwę');
    body.name = name.trim();
    if (line?.quantity !== undefined || !line) {
      const q = Number(quantity.trim());
      if (!Number.isInteger(q) || q < 1 || q > MAX_QUANTITY) return setInvalid('Ilość musi być liczbą całkowitą większą od zera');
      body.quantity = q;
    }
    // a new line sends its category only when one is chosen; „zmień” sends it when changed (null: „Bez kategorii”)
    if (line ? categoryId !== (line.categoryId ?? '') : categoryId !== '') body.categoryId = categoryId === '' ? null : categoryId;
    if (showPrice) {
      if (price.trim() === '') {
        // a line added without a price may stay so; a new one needs it (owner: „muszę podać cenę, wiadomo”)
        if (!line || line.priceGrosze !== null) return setInvalid('Podaj cenę');
      } else {
        try {
          body.priceGrosze = parsePln(price);
        } catch (err) {
          return setInvalid((err as Error).message);
        }
      }
    }
    setInvalid('');
    save.mutate(body);
  }

  return (
    <div className="dialog-backdrop">
      <form
        className="dialog panel form"
        role="dialog"
        aria-modal="true"
        aria-labelledby="custom-line-title"
        data-feature-id="packlists.custom"
        noValidate
        onSubmit={submit}
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
        <DialogLayer onClose={onClose} busy={save.isPending} />
        <h2 id="custom-line-title">Pozycja własna</h2>
        <p className="muted">Pozycja spoza katalogu: nie rezerwuje magazynu, w kosztorysie pojawia się jak reszta sprzętówki.</p>
        {(invalid || (error && error.status !== 409)) && (
          <p className="alert" role="alert">
            {invalid || error!.message}
            {!invalid &&
              error &&
              fieldIssues(error).map((i) => (
                <span key={i.path + i.message}> {i.message}</span>
              ))}
          </p>
        )}
        {error?.status === 409 && <ConflictNotice onReload={() => { void queryClient.invalidateQueries({ queryKey: packlistKey(eventId) }); onClose(); }} />}
        <label className="field">
          <span>Nazwa</span>
          <input autoFocus value={name} maxLength={200} onChange={(e) => setName(e.target.value)} autoComplete="off" placeholder="np. Agregat prądotwórczy" />
        </label>
        {(!line || line.quantity !== undefined) && (
          <label className="field">
            <span>Ilość</span>
            <input inputMode="numeric" value={quantity} onChange={(e) => setQuantity(e.target.value)} autoComplete="off" />
          </label>
        )}
        {/* review M2: whoever does not see the catalog's categories gets no choice (and no label) — the line keeps its own */}
        {categories.length > 0 && (
        <label className="field">
          <span>Kategoria</span>
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">Bez kategorii</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
            {/* a category gone from the list (deleted): still shown, kept unless changed */}
            {categoryId !== '' && !categories.some((c) => c.id === categoryId) && <option value={categoryId}>(usunięta kategoria)</option>}
          </select>
        </label>
        )}
        {showPrice && (
          <label className="field">
            <span>Cena (zł netto)</span>
            <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} autoComplete="off" placeholder="cena jak w kosztorysie, np. 150,00" />
          </label>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Zapisywanie…' : line ? 'Zapisz' : 'Dodaj'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}

/** × of a custom line: asks first, as for the gear. */
export function RemoveCustomDialog({ eventId, line, onClose }: { eventId: string; line: CustomLineView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api(`/api/packlists/custom-lines/${line.id}?version=${line.version}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: packlistKey(eventId) });
      void queryClient.invalidateQueries({ queryKey: QUOTES_KEY });
      onClose();
    },
  });
  const error = remove.error instanceof ApiError ? remove.error : null;
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="remove-custom-title">
        <DialogLayer onClose={onClose} busy={remove.isPending} />
        <h2 id="remove-custom-title">Usunąć ze sprzętówki?</h2>
        <p>„{line.name}”{line.quantity !== undefined ? ` (${line.quantity})` : ''} zniknie ze sprzętówki.</p>
        {error?.status === 409 && <ConflictNotice onReload={() => { void queryClient.invalidateQueries({ queryKey: packlistKey(eventId) }); onClose(); }} />}
        {error && error.status !== 409 && (
          <p className="alert" role="alert">
            {error.message}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="button" autoFocus disabled={remove.isPending} onClick={() => remove.mutate()}>
            {remove.isPending ? 'Usuwanie…' : 'Usuń ze sprzętówki'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
